import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Phone } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithEmail, register, isAuthenticated, isLoading } = useAuth();
  
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
  });

  // Redirect if already authenticated
  React.useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (isRegisterMode) {
        // Register
        if (!formData.name.trim()) {
          throw new Error('Ad Soyad gereklidir');
        }
        if (formData.password.length < 6) {
          throw new Error('Şifre en az 6 karakter olmalıdır');
        }
        
        const result = await register(
          formData.email,
          formData.password,
          formData.name,
          formData.phone || null
        );
        
        if (!result.success) {
          throw new Error(result.error);
        }
        
        navigate('/', { replace: true });
      } else {
        // Login
        const result = await loginWithEmail(formData.email, formData.password);
        
        if (!result.success) {
          throw new Error(result.error);
        }
        
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4 relative z-10">
        <div className="bg-white p-8 shadow-lg" data-testid="login-form-container">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/">
              <img
                src="https://customer-assets.emergentagent.com/job_silk-fular/artifacts/t7z75kaw_VALMERA%20lOGO.png"
                alt="VALMÉRA"
                className="h-12 mx-auto mb-6"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Link>
            <h1 className="text-2xl font-light tracking-wider mb-2">
              {isRegisterMode ? 'ÜYE OL' : 'HOŞ GELDİNİZ'}
            </h1>
            <p className="text-gray-500 text-sm">
              {isRegisterMode 
                ? 'Yeni bir hesap oluşturun' 
                : 'Hesabınıza giriş yapın'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded">
              {error}
              {error.includes('kayıtlı değil') && (
                <button 
                  type="button"
                  onClick={() => {
                    setIsRegisterMode(true);
                    setError('');
                  }}
                  className="block mt-2 text-black underline font-medium"
                >
                  Hemen Kayıt Ol →
                </button>
              )}
            </div>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleEmailSubmit} className="space-y-4 mb-6">
            {isRegisterMode && (
              <>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="name"
                    placeholder="Ad Soyad *"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm"
                    required
                  />
                </div>
                
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Telefon (Opsiyonel)"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm"
                  />
                </div>
              </>
            )}
            
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                name="email"
                placeholder="E-posta *"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm"
                required
              />
            </div>
            
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Şifre *"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {!isRegisterMode && (
              <div className="text-right">
                <Link to="/sifremi-unuttum" className="text-xs text-gray-500 hover:text-black">
                  Şifremi Unuttum
                </Link>
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="auth-submit-button"
              className="w-full bg-black text-white py-3 text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {isRegisterMode ? 'Kayıt Yapılıyor...' : 'Giriş Yapılıyor...'}
                </span>
              ) : (
                isRegisterMode ? 'Üye Ol' : 'Giriş Yap'
              )}
            </button>
          </form>

          {/* Toggle Register/Login */}
          <div className="text-center mb-6">
            <button
              onClick={() => {
                setIsRegisterMode(!isRegisterMode);
                setError('');
                setFormData({ email: '', password: '', name: '', phone: '' });
              }}
              className="text-sm text-gray-600 hover:text-black underline"
            >
              {isRegisterMode 
                ? 'Zaten hesabınız var mı? Giriş Yapın' 
                : 'Hesabınız yok mu? Üye Olun'}
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">veya</span>
            </div>
          </div>

          {/* Google Login/Register Button */}
          <button
            onClick={loginWithGoogle}
            data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 px-6 py-4 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isRegisterMode ? 'Google ile Kayıt Ol' : 'Google ile Giriş Yap'}
          </button>

          {/* Info */}
          <div className="text-center text-xs text-gray-400 mt-6">
            <p>
              Giriş yaparak{' '}
              <a href="#" className="underline">Kullanım Koşulları</a>
              {' '}ve{' '}
              <a href="#" className="underline">Gizlilik Politikası</a>
              'nı kabul etmiş olursunuz.
            </p>
          </div>

          {/* Benefits */}
          <div className="mt-8 pt-8 border-t">
            <h3 className="text-sm font-medium mb-4 text-center">Üyelik Avantajları</h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Favori ürünlerinizi kaydedin
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Sipariş geçmişinizi görüntüleyin
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Özel kampanyalardan haberdar olun
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Hızlı ödeme yapın
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
